import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, TrendingUp, TrendingDown, ExternalLink, Activity } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { countriesAPI } from '../services/api';
import { buildCountryTradeIntro } from '../utils/countryPageCopy';
import LegislationCard from '../components/LegislationCard';

const CountryProfile = () => {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProfile = async () => {
    try {
      const response = await countriesAPI.getProfile(id);
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching country profile:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-slate-500 dark:text-on-surface-variant">Country not found.</p>
      </div>
    );
  }

  const tariffChartData = profile.tariffByCategory?.map(cat => ({
    category: cat.category || 'Unknown',
    'Avg Rate': parseFloat(cat.avg_rate || 0).toFixed(2),
    'Events': parseInt(cat.count || 0),
  })) || [];

  const gti = profile.gti;
  const gtiSectors = profile.gtiSectors || [];
  const tradeEvents = profile.tradeEvents || [];

  const sectorChartData = gtiSectors.map(s => ({
    sector: s.sector,
    score: parseFloat(s.gti_score || 0),
    events: s.event_count || 0,
  }));

  const getEventTypeColor = (type) => {
    const colors = {
      tariff_increase: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      tariff_decrease: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      trade_deal: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      sanction: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      import_ban: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      export_ban: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      quota: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      subsidy: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      anti_dumping: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      dispute: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    };
    return colors[type] || 'bg-slate-100 text-slate-800 dark:bg-secondary-container dark:text-on-secondary-container';
  };

  const formatEventType = (type) =>
    (type || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEOHead
        title={`${profile.country.name} Trade Profile, Tariffs & Sanctions`}
        description={`Explore ${profile.country.name} trade data including tariff rates, import export policies, trade deals, sanctions, and Global Trade Index score. AI-powered trade intelligence for ${profile.country.name}.`}
        path={`/countries/${id}`}
        keywords={`${profile.country.name} tariffs, ${profile.country.name} trade policy, ${profile.country.name} sanctions, ${profile.country.name} import export, ${profile.country.code} trade data, ${profile.country.name} GTI score`}
      />
      <Link
        to="/countries"
        className="flex items-center text-primary-600 dark:text-primary hover:text-primary-700 mb-6"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Countries
      </Link>

      {/* Country header — Stitch-style intelligence band */}
      <header className="mb-8 rounded-3xl border border-slate-200/80 bg-white p-6 transition-colors dark:border-outline-variant/15 dark:bg-surface-container md:p-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start">
            <div className="shrink-0 overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-low p-1">
              <img
                src={`https://flagcdn.com/w160/${(profile.country.code || '').toLowerCase()}.png`}
                alt={`${profile.country.name} flag`}
                width={160}
                height={120}
                className="h-24 w-auto max-w-[160px] rounded-xl object-cover sm:h-[120px]"
                loading="lazy"
              />
            </div>
            <div className="min-w-0">
              <p className="label-intel text-slate-500 dark:text-outline">Intelligence report</p>
              <p className="mt-1 font-mono text-xs text-slate-500 dark:text-on-surface-variant">
                Ref: TR-{String(profile.country.code || 'XX').toUpperCase()}-C{id}
              </p>
              <h1 className="mt-3 font-headline text-3xl font-bold text-slate-900 dark:text-on-surface md:text-4xl">
                {profile.country.name}
              </h1>
              <p className="mt-2 text-slate-600 dark:text-on-surface-variant">Code: {profile.country.code}</p>
              {profile.country.region && (
                <p className="text-slate-600 dark:text-on-surface-variant">
                  Region: {profile.country.region} • {profile.country.continent}
                </p>
              )}
            </div>
          </div>
          {gti && (
            <div className="grid shrink-0 grid-cols-2 gap-3 sm:min-w-[240px]">
              <div className="rounded-2xl border border-outline-variant/15 bg-surface-container-low p-4 dark:bg-surface-container-high/40">
                <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                  GTI
                </p>
                <p
                  className={`mt-1 font-headline text-3xl font-bold ${
                    parseFloat(gti.gti_score) >= 50 ? 'text-emerald-600 dark:text-tertiary' : 'text-red-600 dark:text-error'
                  }`}
                >
                  {parseFloat(gti.gti_score).toFixed(1)}
                </p>
                <div className="mt-1 flex items-center gap-1 text-xs text-slate-500 dark:text-on-surface-variant">
                  {gti.trend === 'improving' ? (
                    <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />
                  ) : gti.trend === 'declining' ? (
                    <TrendingDown className="h-3.5 w-3.5 text-red-500" />
                  ) : null}
                  {gti.score_change ? `${parseFloat(gti.score_change) > 0 ? '+' : ''}${parseFloat(gti.score_change).toFixed(1)}` : 'Stable'}
                </div>
              </div>
              <div className="rounded-2xl border border-outline-variant/15 bg-surface-container-low p-4 dark:bg-surface-container-high/40">
                <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                  Rank
                </p>
                <p className="mt-1 font-headline text-3xl font-bold text-slate-900 dark:text-on-surface">#{gti.gti_rank || '—'}</p>
                <p className="mt-1 text-xs text-slate-500 dark:text-on-surface-variant">{gti.event_count || 0} events</p>
              </div>
            </div>
          )}
        </div>
      </header>

      <section className="mb-8 rounded-stitch-xl border border-slate-200/80 bg-white p-6 dark:border-outline-variant/15 dark:bg-surface-container">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-on-surface mb-2">
          How to read this country profile
        </h2>
        <p className="text-sm text-slate-700 dark:text-on-surface-variant leading-relaxed mb-4">
          {buildCountryTradeIntro(profile.country, profile.statistics)}
        </p>
        <p className="text-xs text-slate-500 dark:text-on-surface-variant mb-2 font-medium uppercase tracking-wide">
          Data provenance
        </p>
        <p className="text-sm text-slate-600 dark:text-on-surface-variant leading-relaxed">
          Metrics combine database tables maintained by TradeRadar (trade events, tariffs, legislation
          excerpts) and automated enrichment from public web sources. Each AI-extracted event links to its
          cited article or filing where available. Aggregates may be revised when sources correct
          themselves or when we fix extraction errors.
        </p>
        <div className="mt-4 flex flex-wrap gap-3 text-sm">
          <Link
            to="/insights/methodology"
            className="text-primary-600 dark:text-primary font-medium hover:underline"
          >
            GTI methodology
          </Link>
          <Link
            to="/editorial-standards"
            className="text-primary-600 dark:text-primary font-medium hover:underline"
          >
            Editorial standards
          </Link>
          <Link to="/insights" className="text-primary-600 dark:text-primary font-medium hover:underline">
            Insights hub
          </Link>
        </div>
      </section>

      {/* 12-col bento: composition, tariffs, timeline, compare */}
      <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="rounded-3xl border border-slate-200/80 bg-white p-6 dark:border-outline-variant/15 dark:bg-surface-container lg:col-span-7">
          <h2 className="mb-4 font-headline text-lg font-semibold text-slate-900 dark:text-on-surface">
            GTI composition by sector
          </h2>
          {sectorChartData.length > 0 ? (
            <div className="space-y-3">
              {sectorChartData.map((s) => (
                <div key={s.sector}>
                  <div className="mb-1 flex justify-between text-xs text-slate-600 dark:text-on-surface-variant">
                    <span className="font-medium text-slate-800 dark:text-on-surface">{s.sector}</span>
                    <span>
                      {Number(s.score).toFixed(0)} GTI · {s.events} evts
                    </span>
                  </div>
                  <div className="h-2.5 overflow-hidden rounded-full bg-slate-100 dark:bg-surface-container-high">
                    <div
                      className="h-full rounded-full bg-primary transition-all dark:bg-primary"
                      style={{ width: `${Math.min(100, Math.max(0, Number(s.score)))}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500 dark:text-on-surface-variant">
              No sector-level GTI breakdown yet. Run the pipeline to populate per-sector scores.
            </p>
          )}
        </div>

        <div className="rounded-3xl border border-slate-200/80 bg-white p-6 dark:border-outline-variant/15 dark:bg-surface-container lg:col-span-5">
          <h2 className="mb-4 font-headline text-lg font-semibold text-slate-900 dark:text-on-surface">
            Tariff pressure by sector
          </h2>
          {tariffChartData.length > 0 ? (
            <ul className="space-y-3">
              {tariffChartData.slice(0, 8).map((row) => {
                const pct = Math.min(100, parseFloat(row['Avg Rate']) * 2);
                return (
                  <li key={row.category}>
                    <div className="mb-1 flex justify-between text-xs text-slate-600 dark:text-on-surface-variant">
                      <span className="font-medium text-slate-800 dark:text-on-surface">{row.category}</span>
                      <span>
                        {row['Avg Rate']}% avg · {row.Events} evts
                      </span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-surface-container-high">
                      <div
                        className="h-full rounded-full bg-tertiary dark:bg-tertiary"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-slate-500 dark:text-on-surface-variant">
              No AI-extracted tariff categories for this country yet.
            </p>
          )}
        </div>

        <div className="rounded-3xl border border-slate-200/80 bg-white p-6 dark:border-outline-variant/15 dark:bg-surface-container lg:col-span-8">
          <h2 className="mb-4 font-headline text-lg font-semibold text-slate-900 dark:text-on-surface">
            Recent trade timeline
          </h2>
          {tradeEvents.length > 0 ? (
            <ul className="space-y-0 border-l-2 border-primary/30 pl-4 dark:border-primary/40">
              {[...tradeEvents]
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .slice(0, 10)
                .map((event) => (
                  <li key={event.id} className="relative pb-6 last:pb-0">
                    <span className="absolute -left-[calc(0.25rem+5px)] top-1.5 h-2.5 w-2.5 rounded-full bg-primary ring-4 ring-white dark:ring-surface-container" />
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded px-2 py-0.5 text-xs font-medium ${getEventTypeColor(event.event_type)}`}>
                        {formatEventType(event.event_type)}
                      </span>
                      <span className="text-xs text-slate-500 dark:text-on-surface-variant">
                        {new Date(event.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-slate-800 dark:text-on-surface-variant">{event.description}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500 dark:text-on-surface-variant">
                      {event.imposing_country && event.target_country && (
                        <span>
                          {event.imposing_country} → {event.target_country}
                        </span>
                      )}
                      {event.source_url && (
                        <a
                          href={event.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-primary-600 dark:text-primary hover:underline"
                        >
                          Source <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </li>
                ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500 dark:text-on-surface-variant">No AI trade events detected for this profile yet.</p>
          )}
        </div>

        <div className="flex flex-col gap-4 lg:col-span-4">
          <Link
            to="/compare"
            className="rounded-3xl border border-primary/25 bg-gradient-to-br from-primary-container/30 to-surface-container p-6 shadow-sm transition hover:border-primary/40 dark:from-primary-container/15 dark:to-surface-container-low"
          >
            <h2 className="font-headline text-lg font-semibold text-slate-900 dark:text-on-surface">Benchmark vs peers</h2>
            <p className="mt-2 text-sm text-slate-600 dark:text-on-surface-variant">
              Open the GTI comparison workspace and add {profile.country.code} alongside other economies.
            </p>
            <span className="mt-4 inline-flex items-center text-sm font-semibold text-primary-600 dark:text-primary">
              Go to Compare
              <ArrowRight className="ml-1 h-4 w-4" />
            </span>
          </Link>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-1">
            <div className="rounded-2xl border border-outline-variant/15 bg-surface-container-low p-4 dark:bg-surface-container-high/30">
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">Avg tariff</p>
              <p className="mt-1 font-headline text-xl font-bold text-slate-900 dark:text-on-surface">
                {profile.statistics.averageTariff > 0 ? `${profile.statistics.averageTariff.toFixed(1)}%` : '—'}
              </p>
            </div>
            <div className="rounded-2xl border border-outline-variant/15 bg-surface-container-low p-4 dark:bg-surface-container-high/30">
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">AI events</p>
              <p className="mt-1 font-headline text-xl font-bold text-slate-900 dark:text-on-surface">{tradeEvents.length}</p>
            </div>
            <div className="rounded-2xl border border-outline-variant/15 bg-surface-container-low p-4 dark:bg-surface-container-high/30">
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">Sanctions / bans</p>
              <p className="mt-1 font-headline text-xl font-bold text-slate-900 dark:text-on-surface">
                {tradeEvents.filter((e) => ['sanction', 'import_ban', 'export_ban'].includes(e.event_type)).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Trade Deals */}
      {profile.recentDeals.length > 0 && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-on-surface mb-4">Active Trade Deals</h2>
          <div className="space-y-4">
            {profile.recentDeals.map((deal) => (
              <div key={deal.id} className="rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container p-6 transition-colors">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-on-surface mb-2">{deal.name}</h3>
                <p className="text-sm text-slate-600 dark:text-on-surface-variant mb-2">Type: {deal.type} • Status: {deal.status}</p>
                {deal.description && (
                  <p className="text-slate-700 dark:text-on-surface-variant">{deal.description}</p>
                )}
                {deal.effective_date && (
                  <p className="text-sm text-slate-500 dark:text-on-surface-variant mt-2">
                    Effective: {new Date(deal.effective_date).toLocaleDateString()}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Legislation */}
      {profile.recentLegislation.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-on-surface mb-4">Recent Legislation</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {profile.recentLegislation.map((legislation) => (
              <LegislationCard key={legislation.id} legislation={legislation} />
            ))}
          </div>
        </div>
      )}

      {/* Empty state if no data at all */}
      {!gti && tradeEvents.length === 0 && profile.recentDeals.length === 0 && profile.recentLegislation.length === 0 && tariffChartData.length === 0 && (
        <div className="text-center py-12 rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container transition-colors">
          <Activity className="mx-auto mb-3 h-12 w-12 text-slate-300 dark:text-outline" />
          <p className="text-slate-500 dark:text-on-surface-variant">No trade intelligence data available yet for this country.</p>
          <p className="mt-1 text-sm text-slate-400 dark:text-outline">
            Run the pipeline to populate data:{' '}
            <code className="rounded-stitch bg-slate-100 px-2 py-0.5 dark:bg-surface-container-high">npm run pipeline</code>
          </p>
        </div>
      )}
    </div>
  );
};

export default CountryProfile;
